import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { themes, Theme } from '../utils/themes';
import { UserContext } from '../store/useStore';
import WordCard from '../components/WordCard';
import { Play, Sparkles, Star, Download, Podcast } from 'lucide-react';
import { ThemeProgressModal } from '../components/themes/ThemeProgressModal';
import AudioPlayer from '../components/common/AudioPlayer';
import { db } from '../lib/firebase';
import { ref, get } from 'firebase/database';

export default function ThemeDetail() {
  const { themeId } = useParams<{ themeId: string }>();
  const navigate = useNavigate();
  const { getWordsFromTheme, wordsTheme, themesProgress } = useContext(UserContext);
  const [theme, setTheme] = useState<Theme | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [podcast, setPodcast] = useState<{ url: string, timestamp: number } | null>(null);

  useEffect(() => {
    if (themeId) {
      const foundTheme = themes.find(t => t.id === themeId);
      if (foundTheme) {
        setTheme(foundTheme);
        getWordsFromTheme(foundTheme.name);
      }

      const fetchPodcast = async () => {
        try {
          const podcastRef = ref(db, `podcasts/${themeId}`);
          const snapshot = await get(podcastRef);
          if (snapshot.exists()) {
            setPodcast(snapshot.val());
          } else {
            setPodcast(null);
          }
        } catch (error) {
          console.error("Error fetching podcast:", error);
        }
      };
      fetchPodcast();
    }
  }, [themeId, getWordsFromTheme]);

  if (!theme) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  const handleStartTraining = () => {
    navigate(`/training?theme=${theme.id}`);
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-purple-50 to-white pb-16'>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Animated CTA Card */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 rounded-2xl shadow-2xl p-8 md:p-12">
            {/* Animated background elements */}
            <motion.div
              className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl"
              animate={{
                x: [0, 100, 0],
                y: [0, -50, 0],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.div
              className="absolute bottom-0 left-0 w-48 h-48 bg-pink-300 opacity-10 rounded-full blur-3xl"
              animate={{
                x: [0, -80, 0],
                y: [0, 60, 0],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />

            {/* Content */}
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
              
              <div className="flex-1 text-center md:text-left">
                <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-4">
                  <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center justify-center md:justify-start gap-3 mb-4"
                >
                  <span className="text-6xl">{theme.icon}</span>
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                      {theme.name}
                    </h1>
                    <p className="text-purple-100 text-lg">
                      Master {theme.words.length} new words
                    </p>
                  </div>
                </motion.div>

                </div>
                
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-purple-50 text-lg mb-6 max-w-2xl"
                >
                  Ready to start your learning journey? Begin interactive training now, or download the printable worksheet to practice offline later!
                </motion.p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                <motion.a
                  href={`/worksheets/${theme.id}.pdf`}
                  download={`${theme.name} Worksheet.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="group relative px-6 py-4 bg-white/20 hover:bg-white/30 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 backdrop-blur-md border border-white/30"
                >
                  <Download className="w-5 h-5 group-hover:-translate-y-1 transition-transform" />
                  <span>Worksheet</span>
                </motion.a>

                <motion.button
                  onClick={handleStartTraining}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="group relative px-8 py-4 bg-white text-purple-600 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3"
                >
                  <Sparkles className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                  <span>Start Training</span>
                  <Play className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  
                  {/* Shine effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20"
                    animate={{
                      x: ['-100%', '100%'],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      repeatDelay: 2,
                    }}
                  />
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {podcast && (
          <div className="mb-12 bg-purple-100 rounded-xl p-6 shadow-lg border border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold text-gray-800 flex items-center">
                <Podcast className="w-6 h-6 mr-2 text-purple-600" />
                Theme Podcast
              </h2>
              <span className="bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full">NEW</span>
            </div>
            <p className="text-gray-600 mb-4">
              Listen to our podcast about this theme to practice your pronunciation and listening skills!
            </p>
            <div className='w-full max-w-md justify-center items-center mx-auto flex flex-col gap-4'>
              <AudioPlayer show={false} audioUrl={podcast.url} word={theme.name} />
              <a
                href={podcast.url}
                download={`${theme.name}-podcast.wav`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-md w-full sm:w-auto self-center"
              >
                <Download className="w-4 h-4" />
                <span>Download Podcast</span>
              </a>
            </div>
          </div>
        )}

        {/* Words and Sentences Section */}
        <div>
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <motion.h2
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-2xl font-semibold text-gray-800"
              >
                What you will learn here
              </motion.h2>

              {/* Stars Display */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                onClick={() => setIsModalOpen(true)}
                className="bg-white rounded-xl p-2 inline-flex items-center gap-2 cursor-pointer hover:bg-purple-50 transition-colors shadow-sm border border-gray-200"
              >
                <div className="flex gap-1">
                  {[1, 2, 3].map((star) => {
                    const progress = themesProgress.find(t => t.theme === theme.name)?.progress || 0;
                    const targetPoints = theme.words.length * 5;
                    const percentage = Math.min(100, Math.floor((progress / targetPoints) * 100)) || 0;
                    const isEarned = percentage >= (star * 33);
                    return (
                      <Star
                        key={star}
                        className={`w-5 h-5 transition-all duration-300 ${
                          isEarned 
                            ? 'fill-yellow-400 text-yellow-400 drop-shadow-sm' 
                            : 'fill-gray-100 text-gray-300'
                        }`}
                      />
                    );
                  })}
                </div>
                <div className="flex items-center gap-1 text-purple-600 text-sm font-medium mr-1">
                  <span>View Progress</span>
                </div>
              </motion.div>
            </div>
            
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              onClick={handleStartTraining}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-2.5 bg-purple-600 text-white rounded-lg font-semibold shadow hover:bg-purple-700 transition-colors flex items-center gap-2 text-sm w-fit"
            >
              <Play className="w-4 h-4" />
              <span>Start Training</span>
            </motion.button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[...(theme.words || []), ...(theme.sentences || [])].map((item, index) => {
              const itemData = wordsTheme?.find(w => w.word === item);
              const isSentence = theme.sentences?.includes(item);
              return (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * Math.min(index, 20) }} // limit delay so it doesn't take forever to animate all
                >
                  <WordCard
                    word={item}
                    isExpression={isSentence}
                    fromTheme={true}
                    themeName={theme.name}
                    lastPractice={itemData?.lastPractice}
                    progress={itemData?.progress}
                  />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      <ThemeProgressModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        themeName={theme.name}
        totalWords={theme.words.length}
        currentProgress={themesProgress.find(t => t.theme === theme.name)?.progress || 0}
      />
    </div>
  );
}
